/**
 * Phase line chart — mobile's light-themed Canvas2D counterpart to web's
 * `web/src/components/room/charts/phaseDraw.ts` (phase half). Same geometry
 * (`computePhaseGeometry`), different ink — see `./_shared.ts`'s module doc.
 * Group Delay lives in the sibling `./group-delay.ts` (shares this pad —
 * both read the same complex transfer function's phase).
 */

import type { ChartPad } from '../layout';
import { computePhaseGeometry } from '../phase';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawSeriesLine, INK } from './_shared';

export const PHASE_PAD: ChartPad = { t: 14, r: 14, b: 26, l: 40 };

export interface DrawPhaseOpts {
  freqs: number[];
  phase: number[];
  phaseBaseline?: number[] | null;
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
}

export function drawPhaseCanvas(canvas: HTMLCanvasElement, opts: DrawPhaseOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computePhaseGeometry({
    freqs: opts.freqs,
    phase: opts.phase,
    phaseBaseline: opts.phaseBaseline,
    width: opts.width,
    height: opts.height,
    pad: PHASE_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    unit: 'deg',
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No frequency response data yet');
    return;
  }

  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);
  if (geo.baselinePoints) {
    drawSeriesLine(ctx, geo.frame, geo.baselinePoints, INK.referenceDashed, 1.3);
  }
  drawSeriesLine(ctx, geo.frame, geo.activePoints, INK.primary, 2.0);
}
